import type {
  CompetitionCreationRequestDto,
  CompetitionRequestApprovalResult,
  PlatformAdminListItemDto,
  PlatformApproveErasureRequest,
  PlatformAuditLogDto,
  PlatformCompetitionPersonDto,
  PlatformCompetitionRoleGrantDto,
  PlatformDashboardDto,
  PlatformErasureRequestDto,
  PlatformGrantCompetitionRoleRequest,
  PlatformJudgeListItemDto,
  PlatformReactivateUserRequest,
  PlatformRejectErasureRequest,
  PlatformSessionsRevokedDto,
  PlatformSuspendUserRequest,
  PlatformUserCompetitionRelationshipDto,
  PlatformUserDetailDto,
  PlatformUserListItemDto,
} from '@napaicp/schemas'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { apiRequest, apiRequestWithMeta } from '@/lib/api-client'
import { platformKeys } from './query-keys'

/**
 * Platform Administration data-fetching hooks (Wave M). Every list hook is
 * server-side paginated/filtered/sorted (UX-010) — none of them ever fetch
 * "everything" and filter client-side. Every mutation hook requires the
 * caller to already hold the matching `platform.*.manage` permission
 * server-side; these hooks do not themselves decide authorization, they only
 * surface the server's answer (401/403 surfaces as `ApiError`).
 */

export interface Page<T> {
  rows: T[]
  total: number
  limit: number
  offset: number
}

// ---------------------------------------------------------------------------
// Dashboard (PR-020)
// ---------------------------------------------------------------------------

export function usePlatformDashboard(enabled: boolean): UseQueryResult<PlatformDashboardDto> {
  return useQuery({
    queryKey: platformKeys.dashboard(),
    queryFn: () => apiRequest<PlatformDashboardDto>('/api/platform/dashboard'),
    enabled,
  })
}

// ---------------------------------------------------------------------------
// Users (PR-014/015/018)
// ---------------------------------------------------------------------------

export interface PlatformUserListFilters {
  search: string
  status: string
  role: string
  competitionId: string
  judgeOnly: boolean
  sort: string
  sortDir: 'asc' | 'desc'
}

export function usePlatformUsers(
  filters: PlatformUserListFilters,
  page: { limit: number; offset: number },
  enabled: boolean,
): UseQueryResult<Page<PlatformUserListItemDto>> {
  const params = new URLSearchParams({
    limit: String(page.limit),
    offset: String(page.offset),
    sort: filters.sort,
    sortDir: filters.sortDir,
  })
  if (filters.search.trim().length > 0) params.set('search', filters.search.trim())
  if (filters.status.length > 0) params.set('status', filters.status)
  if (filters.role.length > 0) params.set('role', filters.role)
  if (filters.competitionId.length > 0) params.set('competitionId', filters.competitionId)
  if (filters.judgeOnly) params.set('judgeOnly', 'true')

  const keyFilters = {
    ...filters,
    judgeOnly: String(filters.judgeOnly),
    limit: String(page.limit),
    offset: String(page.offset),
  }

  return useQuery({
    queryKey: platformKeys.users.list(keyFilters),
    queryFn: async () => {
      const { data, meta } = await apiRequestWithMeta<PlatformUserListItemDto[]>(
        `/api/platform/users?${params.toString()}`,
      )
      return {
        rows: data,
        total: Number(meta.total ?? data.length),
        limit: Number(meta.limit ?? page.limit),
        offset: Number(meta.offset ?? page.offset),
      }
    },
    enabled,
  })
}

export function usePlatformUserDetail(
  userId: string,
  enabled: boolean,
): UseQueryResult<PlatformUserDetailDto> {
  return useQuery({
    queryKey: platformKeys.users.detail(userId),
    queryFn: () => apiRequest<PlatformUserDetailDto>(`/api/platform/users/${userId}`),
    enabled: enabled && userId.length > 0,
  })
}

export function usePlatformUserCompetitions(
  userId: string,
  enabled: boolean,
): UseQueryResult<PlatformUserCompetitionRelationshipDto[]> {
  return useQuery({
    queryKey: platformKeys.users.competitions(userId),
    queryFn: () =>
      apiRequest<PlatformUserCompetitionRelationshipDto[]>(
        `/api/platform/users/${userId}/competitions`,
      ),
    enabled: enabled && userId.length > 0,
  })
}

function invalidateUser(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: platformKeys.users.detail(userId) }),
    queryClient.invalidateQueries({ queryKey: platformKeys.users.all() }),
    queryClient.invalidateQueries({ queryKey: platformKeys.dashboard() }),
    // Grant/Revoke Super Admin and Suspend/Reactivate all change fields the
    // Platform Administrators list (Gap 3) renders (accountStatus, or
    // membership in the super_admin set itself) — keep it in the same
    // invalidation set as every other user-targeting mutation rather than
    // adding a one-off invalidation at each call site.
    queryClient.invalidateQueries({ queryKey: platformKeys.admins.all() }),
  ]).then(() => undefined)
}

export function useSuspendUser(userId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: PlatformSuspendUserRequest) =>
      apiRequest<PlatformUserDetailDto>(`/api/platform/users/${userId}/suspend`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateUser(queryClient, userId),
  })
}

export function useReactivateUser(userId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: PlatformReactivateUserRequest) =>
      apiRequest<PlatformUserDetailDto>(`/api/platform/users/${userId}/reactivate`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateUser(queryClient, userId),
  })
}

export function useRevokeUserSessions(userId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { reason: string }) =>
      apiRequest<PlatformSessionsRevokedDto>(`/api/platform/users/${userId}/revoke-sessions`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateUser(queryClient, userId),
  })
}

export function useGrantSuperAdmin(userId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { reason: string }) =>
      apiRequest<PlatformUserDetailDto>(`/api/platform/admins/${userId}/grant`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateUser(queryClient, userId),
  })
}

export function useRevokeSuperAdmin(userId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { reason: string }) =>
      apiRequest<PlatformUserDetailDto>(`/api/platform/admins/${userId}/revoke`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateUser(queryClient, userId),
  })
}

export function useGrantCompetitionRole(userId: string, competitionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: PlatformGrantCompetitionRoleRequest) =>
      apiRequest<PlatformCompetitionRoleGrantDto>(
        `/api/platform/competitions/${competitionId}/roles`,
        { method: 'POST', body },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: platformKeys.users.competitions(userId) })
      await queryClient.invalidateQueries({ queryKey: platformKeys.users.all() })
    },
  })
}

export function useRevokeCompetitionRole(userId: string, competitionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ role, reason }: { role: string; reason: string }) =>
      apiRequest(`/api/platform/competitions/${competitionId}/roles/${userId}/${role}`, {
        method: 'DELETE',
        body: { reason },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: platformKeys.users.competitions(userId) })
      await queryClient.invalidateQueries({ queryKey: platformKeys.users.all() })
    },
  })
}

// ---------------------------------------------------------------------------
// Competition People (PR-016)
// ---------------------------------------------------------------------------

export interface PlatformPeopleFilters {
  search: string
  role: string
  sort: string
  sortDir: 'asc' | 'desc'
}

export function usePlatformCompetitionPeople(
  competitionId: string,
  filters: PlatformPeopleFilters,
  page: { limit: number; offset: number },
  enabled: boolean,
): UseQueryResult<Page<PlatformCompetitionPersonDto>> {
  const params = new URLSearchParams({
    limit: String(page.limit),
    offset: String(page.offset),
    sort: filters.sort,
    sortDir: filters.sortDir,
  })
  if (filters.search.trim().length > 0) params.set('search', filters.search.trim())
  if (filters.role.length > 0) params.set('role', filters.role)

  const keyFilters = { ...filters, limit: String(page.limit), offset: String(page.offset) }

  return useQuery({
    queryKey: platformKeys.people.list(competitionId, keyFilters),
    queryFn: async () => {
      const { data, meta } = await apiRequestWithMeta<PlatformCompetitionPersonDto[]>(
        `/api/platform/competitions/${competitionId}/people?${params.toString()}`,
      )
      return {
        rows: data,
        total: Number(meta.total ?? data.length),
        limit: Number(meta.limit ?? page.limit),
        offset: Number(meta.offset ?? page.offset),
      }
    },
    enabled: enabled && competitionId.length > 0,
  })
}

// ---------------------------------------------------------------------------
// Platform Judges (PR-017)
// ---------------------------------------------------------------------------

export interface PlatformJudgeFilters {
  search: string
  competitionId: string
  sort: string
  sortDir: 'asc' | 'desc'
}

export function usePlatformJudges(
  filters: PlatformJudgeFilters,
  page: { limit: number; offset: number },
  enabled: boolean,
): UseQueryResult<Page<PlatformJudgeListItemDto>> {
  const params = new URLSearchParams({
    limit: String(page.limit),
    offset: String(page.offset),
    sort: filters.sort,
    sortDir: filters.sortDir,
  })
  if (filters.search.trim().length > 0) params.set('search', filters.search.trim())
  if (filters.competitionId.length > 0) params.set('competitionId', filters.competitionId)

  const keyFilters = { ...filters, limit: String(page.limit), offset: String(page.offset) }

  return useQuery({
    queryKey: platformKeys.judges.list(keyFilters),
    queryFn: async () => {
      const { data, meta } = await apiRequestWithMeta<PlatformJudgeListItemDto[]>(
        `/api/platform/judges?${params.toString()}`,
      )
      return {
        rows: data,
        total: Number(meta.total ?? data.length),
        limit: Number(meta.limit ?? page.limit),
        offset: Number(meta.offset ?? page.offset),
      }
    },
    enabled,
  })
}

// ---------------------------------------------------------------------------
// Platform Administrators (PR-019, Gap 1/3 closeout) — list is read-only;
// Grant/Revoke reuse `useGrantSuperAdmin`/`useRevokeSuperAdmin` above.
// ---------------------------------------------------------------------------

export interface PlatformAdminFilters {
  search: string
  sort: string
  sortDir: 'asc' | 'desc'
}

export function usePlatformAdmins(
  filters: PlatformAdminFilters,
  page: { limit: number; offset: number },
  enabled: boolean,
): UseQueryResult<Page<PlatformAdminListItemDto>> {
  const params = new URLSearchParams({
    limit: String(page.limit),
    offset: String(page.offset),
    sort: filters.sort,
    sortDir: filters.sortDir,
  })
  if (filters.search.trim().length > 0) params.set('search', filters.search.trim())

  const keyFilters = { ...filters, limit: String(page.limit), offset: String(page.offset) }

  return useQuery({
    queryKey: platformKeys.admins.list(keyFilters),
    queryFn: async () => {
      const { data, meta } = await apiRequestWithMeta<PlatformAdminListItemDto[]>(
        `/api/platform/admins?${params.toString()}`,
      )
      return {
        rows: data,
        total: Number(meta.total ?? data.length),
        limit: Number(meta.limit ?? page.limit),
        offset: Number(meta.offset ?? page.offset),
      }
    },
    enabled,
  })
}

// ---------------------------------------------------------------------------
// Competition Creation Requests (Wave O, GOV-019/020, UX-017/018) — the
// review queue. Every mutation here requires the caller to already hold
// `platform.competitions.approve` server-side (COMPETITIONS_APPROVE); these
// hooks only surface the server's 401/403 as an `ApiError`, they never
// decide authorization themselves.
// ---------------------------------------------------------------------------

export interface PlatformCompetitionRequestFilters {
  status: string
}

export function usePlatformCompetitionRequests(
  filters: PlatformCompetitionRequestFilters,
  page: { limit: number; offset: number },
  enabled: boolean,
): UseQueryResult<Page<CompetitionCreationRequestDto>> {
  const params = new URLSearchParams({ limit: String(page.limit), offset: String(page.offset) })
  if (filters.status.length > 0) params.set('status', filters.status)

  const keyFilters = { ...filters, limit: String(page.limit), offset: String(page.offset) }

  return useQuery({
    queryKey: platformKeys.competitionRequests.list(keyFilters),
    queryFn: async () => {
      const { data, meta } = await apiRequestWithMeta<CompetitionCreationRequestDto[]>(
        `/api/platform/competition-requests?${params.toString()}`,
      )
      return {
        rows: data,
        total: Number(meta.total ?? data.length),
        limit: Number(meta.limit ?? page.limit),
        offset: Number(meta.offset ?? page.offset),
      }
    },
    enabled,
  })
}

export function usePlatformCompetitionRequestDetail(
  requestId: string,
  enabled: boolean,
): UseQueryResult<CompetitionCreationRequestDto> {
  return useQuery({
    queryKey: platformKeys.competitionRequests.detail(requestId),
    queryFn: () =>
      apiRequest<CompetitionCreationRequestDto>(`/api/platform/competition-requests/${requestId}`),
    enabled: enabled && requestId.length > 0,
  })
}

function invalidateCompetitionRequest(
  queryClient: ReturnType<typeof useQueryClient>,
  requestId: string,
): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: platformKeys.competitionRequests.detail(requestId) }),
    queryClient.invalidateQueries({ queryKey: platformKeys.competitionRequests.all() }),
  ]).then(() => undefined)
}

export function useStartReviewCompetitionRequest(requestId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiRequest<CompetitionCreationRequestDto>(
        `/api/platform/competition-requests/${requestId}/start-review`,
        { method: 'POST' },
      ),
    onSuccess: () => invalidateCompetitionRequest(queryClient, requestId),
  })
}

export function useRequestChangesCompetitionRequest(requestId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { reason: string }) =>
      apiRequest<CompetitionCreationRequestDto>(
        `/api/platform/competition-requests/${requestId}/request-changes`,
        { method: 'POST', body },
      ),
    onSuccess: () => invalidateCompetitionRequest(queryClient, requestId),
  })
}

export function useRejectCompetitionRequest(requestId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { reason: string }) =>
      apiRequest<CompetitionCreationRequestDto>(
        `/api/platform/competition-requests/${requestId}/reject`,
        { method: 'POST', body },
      ),
    onSuccess: () => invalidateCompetitionRequest(queryClient, requestId),
  })
}

export function useApproveCompetitionRequest(requestId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { reason: string }) =>
      apiRequest<CompetitionRequestApprovalResult>(
        `/api/platform/competition-requests/${requestId}/approve`,
        { method: 'POST', body },
      ),
    onSuccess: () => invalidateCompetitionRequest(queryClient, requestId),
  })
}

// ---------------------------------------------------------------------------
// Platform Audit (PR-021) — read-only
// ---------------------------------------------------------------------------

export interface PlatformAuditFilters {
  actorId: string
  targetUserId: string
  competitionId: string
  action: string
  from: string
  to: string
}

export function usePlatformAuditLogs(
  filters: PlatformAuditFilters,
  page: { limit: number; offset: number },
  enabled: boolean,
): UseQueryResult<Page<PlatformAuditLogDto>> {
  const params = new URLSearchParams({
    limit: String(page.limit),
    offset: String(page.offset),
  })
  for (const [key, value] of Object.entries(filters) as [string, string][]) {
    if (value.trim().length > 0) params.set(key, value.trim())
  }

  const keyFilters = { ...filters, limit: String(page.limit), offset: String(page.offset) }

  return useQuery({
    queryKey: platformKeys.audit.list(keyFilters),
    queryFn: async () => {
      const { data, meta } = await apiRequestWithMeta<PlatformAuditLogDto[]>(
        `/api/platform/audit-logs?${params.toString()}`,
      )
      return {
        rows: data,
        total: Number(meta.total ?? data.length),
        limit: Number(meta.limit ?? page.limit),
        offset: Number(meta.offset ?? page.offset),
      }
    },
    enabled,
  })
}

// ---------------------------------------------------------------------------
// Account Erasure — Platform Admin review/execute (PRIV-003/004, API-100/101, v3.98)
// ---------------------------------------------------------------------------

export function usePlatformErasureRequests(
  status: string,
  page: { limit: number; offset: number },
  enabled: boolean,
): UseQueryResult<Page<PlatformErasureRequestDto>> {
  const params = new URLSearchParams({
    limit: String(page.limit),
    offset: String(page.offset),
  })
  if (status.length > 0) params.set('status', status)

  const keyFilters = { status, limit: String(page.limit), offset: String(page.offset) }

  return useQuery({
    queryKey: platformKeys.erasureRequests.list(keyFilters),
    queryFn: async () => {
      const { data, meta } = await apiRequestWithMeta<PlatformErasureRequestDto[]>(
        `/api/platform/users/erasure-requests?${params.toString()}`,
      )
      return {
        rows: data,
        total: Number(meta.total ?? data.length),
        limit: Number(meta.limit ?? page.limit),
        offset: Number(meta.offset ?? page.offset),
      }
    },
    enabled,
  })
}

function invalidateErasureRequests(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: platformKeys.erasureRequests.all() }),
    queryClient.invalidateQueries({ queryKey: platformKeys.users.all() }),
    queryClient.invalidateQueries({ queryKey: platformKeys.dashboard() }),
  ]).then(() => undefined)
}

/** Approves AND immediately executes the erasure — see `services/account-erasure-service.ts` for what "execute" does. */
export function useApproveErasureRequest(requestId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: PlatformApproveErasureRequest) =>
      apiRequest<PlatformErasureRequestDto>(
        `/api/platform/users/erasure-requests/${requestId}/approve`,
        { method: 'POST', body },
      ),
    onSuccess: () => invalidateErasureRequests(queryClient),
  })
}

export function useRejectErasureRequest(requestId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: PlatformRejectErasureRequest) =>
      apiRequest<PlatformErasureRequestDto>(
        `/api/platform/users/erasure-requests/${requestId}/reject`,
        { method: 'POST', body },
      ),
    onSuccess: () => invalidateErasureRequests(queryClient),
  })
}
